// Phase 3D.6-B - Pure, deterministic CONSOLIDATED PILOT SCENARIO SUMMARY engine
// for the Savoie pilot. It consolidates the FOUR canonical outputs of the
// existing engines into a single, read-only, transparency-first summary:
//
//   * selectGrowthOpportunity(dataset)          -> opportunityResult
//   * buildGrowthRecommendation(opportunity)    -> recommendationResult
//   * buildSimulatedCampaign(recommendation)    -> campaignResult
//   * buildSimulatedCampaignOutcomes(campaign)  -> outcomesResult
//
// SCOPE / SAFETY CONTRACT:
//   * PURE, SYNCHRONOUS, DETERMINISTIC. No side effects.
//   * Imports NOTHING (no engine, no network, no storage).
//   * NO fetch / NO axios / NO DNS, NO DB, NO AI/provider, NO ad platform.
//   * NO Date / NO Date.now / NO performance.now, NO randomness,
//     NO environment read.
//   * Does NOT render, does NOT create a page/route/component/backend.
//   * NEVER recomputes a KPI, an evidence figure, a period, a market, a budget
//     or a scenario metric. It ONLY validates coherence and re-exposes the
//     values already produced by the canonical engines.
//   * NEVER shares a mutable reference (object/array) with its inputs: every
//     nested structure returned is a FRESH copy built from an explicit
//     allow-list of fields (no global spread, no full clone).
//   * NEVER emits a forbidden / misleading field (see FORBIDDEN_KEYS below).
//   * Fail-closed: any contract violation yields an explicit { status:"none" }.

// Canonical constants -------------------------------------------------------
export const SUMMARY_TYPE = "synthetic_pilot_scenario";

// The single supported recommendation action type in this pilot phase.
const CANONICAL_ACTION_TYPE = "audience_focus";

// Exact synthesis disclaimer (single JS string, NO injected newline). It is
// DISTINCT from and does NOT replace the four source disclaimers.
export const PILOT_SUMMARY_DISCLAIMER =
  "Synthèse de démonstration : les données historiques sont synthétiques, la " +
  "recommandation est une démonstration déterministe, la campagne est " +
  "entièrement fictive et les résultats affichés sont simulés. Ces résultats " +
  "ne sont ni observés ni prédictifs et ne constituent ni une prévision, ni " +
  "une probabilité, ni une garantie, ni un ROI. Aucune campagne réelle n'est " +
  "créée, programmée, publiée ni activée, et cette démonstration n'a aucune " +
  "affiliation avec une organisation réelle.";

// Deterministic reason tokens for the explicit "none" result. The ORDER of the
// gates below mirrors this documented list; the FIRST invalid gate wins.
export const SUMMARY_NONE_REASONS = Object.freeze({
  INVALID_INPUT: "invalid_input",
  INVALID_OPPORTUNITY: "invalid_opportunity",
  INVALID_RECOMMENDATION: "invalid_recommendation",
  INVALID_CAMPAIGN: "invalid_campaign",
  INVALID_OUTCOMES: "invalid_outcomes",
  OPPORTUNITY_ID_MISMATCH: "opportunity_id_mismatch",
  RECOMMENDATION_ID_MISMATCH: "recommendation_id_mismatch",
  CAMPAIGN_ID_MISMATCH: "campaign_id_mismatch",
  MARKET_MISMATCH: "market_mismatch",
  PERIOD_MISMATCH: "period_mismatch",
  EVIDENCE_MISMATCH: "evidence_mismatch",
  INVALID_PROVENANCE: "invalid_provenance",
  INVALID_META: "invalid_meta",
  UNSAFE_SIMULATION_FLAGS: "unsafe_simulation_flags",
  ACTIVATION_NOT_DISABLED: "activation_not_disabled",
  PUBLISH_NOT_DISABLED: "publish_not_disabled",
  EXTERNAL_ID_NOT_NULL: "external_id_not_null",
  OUTCOMES_OBSERVED: "outcomes_observed",
  OUTCOMES_PREDICTIVE: "outcomes_predictive",
  OUTCOMES_FORECAST: "outcomes_forecast",
  MISSING_DISCLAIMER: "missing_disclaimer",
});

// Keys that must NEVER appear anywhere in a successful summary (checked by the
// test suite recursively on KEY NAMES only, never on disclaimer text values).
export const FORBIDDEN_KEYS = Object.freeze([
  "expected_results",
  "forecast",
  "predicted_results",
  "expected_roi",
  "guaranteed_results",
  "probability",
  "confidence_score",
  "real_results",
  "observed_results",
  "activation_url",
  "publish_url",
  "external_platform",
  "live_campaign_id",
]);

// --- Pure validators -------------------------------------------------------
const _isObj = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const _isNonEmptyStr = (v) => typeof v === "string" && v.length > 0;
const _isFiniteNum = (v) => typeof v === "number" && Number.isFinite(v);
const _isNonNegNum = (v) => _isFiniteNum(v) && v >= 0;

// "YYYY-MM" with month 01..12 -> true ; otherwise false.
function _isValidPeriod(str) {
  if (typeof str !== "string") return false;
  const m = /^(\d{4})-(\d{2})$/.exec(str);
  if (!m) return false;
  const month = Number(m[2]);
  return month >= 1 && month <= 12;
}

// Demo meta shape shared by opportunity / recommendation / campaign.source_meta
// (simulation === false variant).
function _demoMetaConform(meta) {
  if (!_isObj(meta)) return false;
  if (meta.is_demo !== true) return false;
  if (meta.provenance !== "synthetic_demo") return false;
  if (meta.provenance_label !== "DONNÉES DE DÉMONSTRATION") return false;
  if (meta.affiliation !== "none") return false;
  return true;
}

// Simulation meta shape used by campaign.meta and outcomes.meta.
function _simMetaConform(meta) {
  if (!_demoMetaConform(meta)) return false;
  if (meta.simulation !== true) return false;
  if (meta.simulation_label !== "SIMULATION") return false;
  return true;
}

// Fail-closed result. Meta is preserved ONLY as a FRESH copy of a fully
// conforming simulation meta (from the outcomes candidate), otherwise null.
function _none(reason, metaCandidate) {
  return {
    status: "none",
    summary: null,
    reason,
    meta: _simMetaConform(metaCandidate)
      ? {
          is_demo: true,
          provenance: "synthetic_demo",
          provenance_label: "DONNÉES DE DÉMONSTRATION",
          affiliation: "none",
          simulation: true,
          simulation_label: "SIMULATION",
        }
      : null,
    simulation: true,
    contains_simulation: true,
    observed: false,
    predictive: false,
    not_a_forecast: true,
    activation_status: "disabled",
    publish_status: "disabled",
    external_campaign_id: null,
  };
}

// --- Structural validators (shape/presence/format/numeric ONLY) ------------
// They deliberately DO NOT check provenance, meta, simulation flags,
// activation/publish/external ids or disclaimers: those violations must reach
// their own, more precise security reasons in the cross-cutting gates below.
function _opportunityStructureValid(o) {
  if (!_isObj(o)) return false;
  if (o.status !== "detected") return false;
  if (!_isNonEmptyStr(o.id)) return false;
  if (!_isNonEmptyStr(o.market_code)) return false;
  if (!_isNonEmptyStr(o.market_name)) return false;
  if (!_isValidPeriod(o.period)) return false;
  if (!_isValidPeriod(o.comparison_period)) return false;
  const ev = o.evidence;
  if (!_isObj(ev)) return false;
  if (!_isNonNegNum(ev.visitor_arrivals)) return false;
  if (!_isNonNegNum(ev.previous_year_visitor_arrivals)) return false;
  if (!_isFiniteNum(ev.growth_percent)) return false;
  if (!_isNonNegNum(ev.market_share_percent)) return false;
  return true;
}

function _recommendationStructureValid(r) {
  if (!_isObj(r)) return false;
  if (r.status !== "recommended") return false;
  if (!_isNonEmptyStr(r.id)) return false;
  if (!_isNonEmptyStr(r.based_on_opportunity_id)) return false;
  if (r.action_type !== CANONICAL_ACTION_TYPE) return false;
  if (r.campaign !== null) return false;
  const aud = r.audience;
  if (!_isObj(aud)) return false;
  if (!_isNonEmptyStr(aud.source_market_code)) return false;
  if (!_isNonEmptyStr(aud.source_market_name)) return false;
  if (!_isValidPeriod(r.evidence_period)) return false;
  if (!_isValidPeriod(r.comparison_period)) return false;
  if (!_isValidPeriod(r.proposed_monitoring_period)) return false;
  const ev = r.evidence_used;
  if (!_isObj(ev)) return false;
  if (!_isNonNegNum(ev.visitor_arrivals)) return false;
  if (!_isNonNegNum(ev.previous_year_visitor_arrivals)) return false;
  if (!_isFiniteNum(ev.growth_percent)) return false;
  if (!_isNonNegNum(ev.market_share_percent)) return false;
  return true;
}

function _campaignStructureValid(c) {
  if (!_isObj(c)) return false;
  if (c.status !== "simulated") return false;
  if (!_isNonEmptyStr(c.id)) return false;
  if (!_isNonEmptyStr(c.based_on_recommendation_id)) return false;
  if (!_isNonEmptyStr(c.based_on_opportunity_id)) return false;
  if (c.expected_results !== null) return false;
  const aud = c.audience;
  if (!_isObj(aud)) return false;
  if (!_isNonEmptyStr(aud.source_market_code)) return false;
  if (!_isNonEmptyStr(aud.source_market_name)) return false;
  const period = c.period;
  if (!_isObj(period)) return false;
  if (!_isValidPeriod(period.month)) return false;
  if (period.type !== "synthetic_demo_period") return false;
  const budget = c.budget;
  if (!_isObj(budget)) return false;
  if (!_isFiniteNum(budget.amount) || budget.amount <= 0) return false;
  if (budget.currency !== "EUR") return false;
  if (budget.assumption !== true) return false;
  const channels = c.channels;
  if (!Array.isArray(channels) || channels.length === 0) return false;
  for (const ch of channels) {
    if (!_isObj(ch) || ch.assumption !== true) return false;
  }
  const objectives = c.objectives;
  if (!Array.isArray(objectives) || objectives.length === 0) return false;
  for (const ob of objectives) {
    if (!_isObj(ob) || ob.assumption !== true) return false;
  }
  const er = c.evidence_reference;
  if (!_isObj(er)) return false;
  if (!_isValidPeriod(er.evidence_period)) return false;
  if (!_isValidPeriod(er.comparison_period)) return false;
  if (!_isFiniteNum(er.growth_percent)) return false;
  if (!_isNonNegNum(er.visitor_arrivals)) return false;
  if (!_isNonNegNum(er.market_share_percent)) return false;
  return true;
}

function _fourMetrics(v) {
  return (
    _isObj(v) &&
    _isNonNegNum(v.scenario_impressions) &&
    _isNonNegNum(v.scenario_reach) &&
    _isNonNegNum(v.scenario_clicks) &&
    _isNonNegNum(v.scenario_conversions)
  );
}

function _outcomesStructureValid(x) {
  if (!_isObj(x)) return false;
  if (x.status !== "scenario") return false;
  if (!_isNonEmptyStr(x.id)) return false;
  if (!_isNonEmptyStr(x.based_on_campaign_id)) return false;
  if (!_isNonEmptyStr(x.based_on_recommendation_id)) return false;
  if (!_isNonEmptyStr(x.based_on_opportunity_id)) return false;
  const ev = x.evidence;
  if (!_isObj(ev)) return false;
  if (!_isValidPeriod(ev.evidence_period)) return false;
  if (!_isValidPeriod(ev.comparison_period)) return false;
  if (!_isFiniteNum(ev.growth_percent)) return false;
  if (!_isNonNegNum(ev.visitor_arrivals)) return false;
  if (!_isNonNegNum(ev.market_share_percent)) return false;
  if (!_isObj(x.assumptions)) return false;
  const sc = x.scenario_constants;
  if (!_isObj(sc)) return false;
  if (!_isFiniteNum(sc.synthetic_cpm_eur)) return false;
  if (!_isFiniteNum(sc.synthetic_avg_frequency)) return false;
  if (!_isFiniteNum(sc.synthetic_ctr)) return false;
  if (!_isFiniteNum(sc.synthetic_conversion_rate)) return false;
  if (!_fourMetrics(x.scenario_outcomes)) return false;
  if (!_fourMetrics(x.scenario_outcomes_display)) return false;
  return true;
}

// --- Fresh-copy builders (explicit allow-lists, no global spread) ----------
function _buildAnalyticalEvidence(o) {
  const ev = o.evidence;
  return {
    evidence_period: o.period,
    comparison_period: o.comparison_period,
    visitor_arrivals: ev.visitor_arrivals,
    previous_year_visitor_arrivals: ev.previous_year_visitor_arrivals,
    growth_percent: ev.growth_percent,
    market_share_percent: ev.market_share_percent,
  };
}

function _buildGrowthOpportunity(o) {
  return {
    id: o.id,
    market_code: o.market_code,
    market_name: o.market_name,
    period: o.period,
    comparison_period: o.comparison_period,
    provenance: o.provenance,
    simulation: o.simulation,
  };
}

function _buildMarketingRecommendation(r) {
  return {
    id: r.id,
    based_on_opportunity_id: r.based_on_opportunity_id,
    action_type: r.action_type,
    audience: {
      source_market_code: r.audience.source_market_code,
      source_market_name: r.audience.source_market_name,
    },
    evidence_period: r.evidence_period,
    comparison_period: r.comparison_period,
    proposed_monitoring_period: r.proposed_monitoring_period,
    provenance: r.provenance,
    simulation: r.simulation,
  };
}

function _buildCampaignAssumptions(c) {
  return {
    id: c.id,
    based_on_recommendation_id: c.based_on_recommendation_id,
    based_on_opportunity_id: c.based_on_opportunity_id,
    audience: {
      source_market_code: c.audience.source_market_code,
      source_market_name: c.audience.source_market_name,
    },
    // period.assumption / period.source are NOT present on the canonical
    // campaign.period ({ month, type }); only existing keys are re-exposed
    // (no field is invented).
    period: {
      month: c.period.month,
      type: c.period.type,
    },
    budget: {
      amount: c.budget.amount,
      currency: c.budget.currency,
      assumption: c.budget.assumption,
      source: c.budget.source,
    },
    channels: c.channels.map((ch) => ({
      code: ch.code,
      label: ch.label,
      assumption: ch.assumption,
      source: ch.source,
    })),
    objectives: c.objectives.map((ob) => ({
      code: ob.code,
      label: ob.label,
      assumption: ob.assumption,
      source: ob.source,
    })),
    assumptions: Array.isArray(c.assumptions)
      ? c.assumptions.map((a) => ({
          kind: a.kind,
          detail: a.detail,
          assumption: a.assumption,
          source: a.source,
          real_data: a.real_data,
          predictive: a.predictive,
        }))
      : [],
    provenance: c.provenance,
    simulation: c.simulation,
    activation_status: c.activation_status,
    publish_status: c.publish_status,
    external_campaign_id: c.external_campaign_id,
  };
}

function _buildSimulatedOutcomes(x) {
  const sc = x.scenario_constants;
  const so = x.scenario_outcomes;
  const sod = x.scenario_outcomes_display;
  return {
    id: x.id,
    based_on_campaign_id: x.based_on_campaign_id,
    based_on_recommendation_id: x.based_on_recommendation_id,
    based_on_opportunity_id: x.based_on_opportunity_id,
    scenario_constants: {
      synthetic_cpm_eur: sc.synthetic_cpm_eur,
      synthetic_avg_frequency: sc.synthetic_avg_frequency,
      synthetic_ctr: sc.synthetic_ctr,
      synthetic_conversion_rate: sc.synthetic_conversion_rate,
    },
    scenario_outcomes: {
      scenario_impressions: so.scenario_impressions,
      scenario_reach: so.scenario_reach,
      scenario_clicks: so.scenario_clicks,
      scenario_conversions: so.scenario_conversions,
    },
    scenario_outcomes_display: {
      scenario_impressions: sod.scenario_impressions,
      scenario_reach: sod.scenario_reach,
      scenario_clicks: sod.scenario_clicks,
      scenario_conversions: sod.scenario_conversions,
    },
    provenance: x.provenance,
    simulation: x.simulation,
    observed: x.observed,
    predictive: x.predictive,
    not_a_forecast: x.not_a_forecast,
    activation_status: x.activation_status,
    publish_status: x.publish_status,
    external_campaign_id: x.external_campaign_id,
  };
}

/**
 * Consolidate the FOUR canonical outputs into a single read-only summary.
 * Never mutates its inputs. Fail-closed on any contract violation.
 * @param {{opportunityResult:object, recommendationResult:object,
 *          campaignResult:object, outcomesResult:object}} args
 * @returns {object} ready summary OR explicit { status:"none", ... }.
 */
export function buildPilotScenarioSummary(args) {
  const R = SUMMARY_NONE_REASONS;

  // --- Gate 1: argument contract (named object with the four entries). ----
  if (!_isObj(args)) return _none(R.INVALID_INPUT, null);
  const o = args.opportunityResult;
  const r = args.recommendationResult;
  const c = args.campaignResult;
  const x = args.outcomesResult;
  if (!_isObj(o) || !_isObj(r) || !_isObj(c) || !_isObj(x)) {
    return _none(R.INVALID_INPUT, null);
  }

  // --- Gates 2-5: per-object structural validation. -----------------------
  if (!_opportunityStructureValid(o)) return _none(R.INVALID_OPPORTUNITY, x.meta);
  if (!_recommendationStructureValid(r)) return _none(R.INVALID_RECOMMENDATION, x.meta);
  if (!_campaignStructureValid(c)) return _none(R.INVALID_CAMPAIGN, x.meta);
  if (!_outcomesStructureValid(x)) return _none(R.INVALID_OUTCOMES, x.meta);

  // --- Gates 6-8: identity continuity (no reconstruction on mismatch). ----
  if (
    r.based_on_opportunity_id !== o.id ||
    c.based_on_opportunity_id !== o.id ||
    x.based_on_opportunity_id !== o.id
  ) {
    return _none(R.OPPORTUNITY_ID_MISMATCH, x.meta);
  }
  if (
    c.based_on_recommendation_id !== r.id ||
    x.based_on_recommendation_id !== r.id
  ) {
    return _none(R.RECOMMENDATION_ID_MISMATCH, x.meta);
  }
  if (x.based_on_campaign_id !== c.id) {
    return _none(R.CAMPAIGN_ID_MISMATCH, x.meta);
  }

  // --- Gate 9: market consistency (outcomes tie via the id chain above). --
  if (
    o.market_code !== r.audience.source_market_code ||
    o.market_code !== c.audience.source_market_code ||
    o.market_name !== r.audience.source_market_name ||
    o.market_name !== c.audience.source_market_name
  ) {
    return _none(R.MARKET_MISMATCH, x.meta);
  }

  // --- Gate 10: period consistency (compare only; never recompute). -------
  if (
    o.period !== r.evidence_period ||
    o.period !== c.evidence_reference.evidence_period ||
    o.period !== x.evidence.evidence_period ||
    o.comparison_period !== r.comparison_period ||
    o.comparison_period !== c.evidence_reference.comparison_period ||
    o.comparison_period !== x.evidence.comparison_period ||
    c.period.month !== r.proposed_monitoring_period
  ) {
    return _none(R.PERIOD_MISMATCH, x.meta);
  }

  // --- Gate 11: decisional evidence consistency (strict, no rounding). ----
  if (
    o.evidence.visitor_arrivals !== r.evidence_used.visitor_arrivals ||
    o.evidence.visitor_arrivals !== c.evidence_reference.visitor_arrivals ||
    o.evidence.visitor_arrivals !== x.evidence.visitor_arrivals ||
    o.evidence.previous_year_visitor_arrivals !==
      r.evidence_used.previous_year_visitor_arrivals ||
    o.evidence.growth_percent !== r.evidence_used.growth_percent ||
    o.evidence.growth_percent !== c.evidence_reference.growth_percent ||
    o.evidence.growth_percent !== x.evidence.growth_percent ||
    o.evidence.market_share_percent !== r.evidence_used.market_share_percent ||
    o.evidence.market_share_percent !== c.evidence_reference.market_share_percent ||
    o.evidence.market_share_percent !== x.evidence.market_share_percent
  ) {
    return _none(R.EVIDENCE_MISMATCH, x.meta);
  }

  // --- Gate 12: provenance. -----------------------------------------------
  if (
    o.provenance !== "synthetic_demo" ||
    r.provenance !== "synthetic_demo" ||
    c.provenance !== "synthetic_demo" ||
    x.provenance !== "synthetic_demo"
  ) {
    return _none(R.INVALID_PROVENANCE, x.meta);
  }

  // --- Gate 13: demo / simulation meta (incl. campaign.source_meta). ------
  if (
    !_demoMetaConform(o.meta) ||
    o.meta.simulation !== false ||
    !_demoMetaConform(r.meta) ||
    r.meta.simulation !== false ||
    !_demoMetaConform(c.source_meta) ||
    !_simMetaConform(c.meta) ||
    !_simMetaConform(x.meta)
  ) {
    return _none(R.INVALID_META, x.meta);
  }

  // --- Gate 14: simulation flags. -----------------------------------------
  if (
    o.simulation !== false ||
    r.simulation !== false ||
    c.simulation !== true ||
    x.simulation !== true
  ) {
    return _none(R.UNSAFE_SIMULATION_FLAGS, x.meta);
  }

  // --- Gates 15-17: activation / publish / external id. -------------------
  if (c.activation_status !== "disabled" || x.activation_status !== "disabled") {
    return _none(R.ACTIVATION_NOT_DISABLED, x.meta);
  }
  if (c.publish_status !== "disabled" || x.publish_status !== "disabled") {
    return _none(R.PUBLISH_NOT_DISABLED, x.meta);
  }
  if (c.external_campaign_id !== null || x.external_campaign_id !== null) {
    return _none(R.EXTERNAL_ID_NOT_NULL, x.meta);
  }

  // --- Gates 18-20: outcomes non-observed / non-predictive / non-forecast.
  if (x.observed !== false) return _none(R.OUTCOMES_OBSERVED, x.meta);
  if (x.predictive !== false) return _none(R.OUTCOMES_PREDICTIVE, x.meta);
  if (x.not_a_forecast !== true) return _none(R.OUTCOMES_FORECAST, x.meta);

  // --- Gate 21: all four source disclaimers must be present. --------------
  if (
    !_isNonEmptyStr(o.disclaimer) ||
    !_isNonEmptyStr(r.disclaimer) ||
    !_isNonEmptyStr(c.disclaimer) ||
    !_isNonEmptyStr(x.disclaimer)
  ) {
    return _none(R.MISSING_DISCLAIMER, x.meta);
  }

  // --- SUCCESS: build a fully fresh consolidated structure. ---------------
  return {
    status: "ready",
    summary_type: SUMMARY_TYPE,
    id: `summary:${x.id}`,
    source_ids: {
      opportunity_id: o.id,
      recommendation_id: r.id,
      campaign_id: c.id,
      outcomes_id: x.id,
    },
    provenance: "synthetic_demo",
    simulation: true,
    contains_simulation: true,
    observed: false,
    predictive: false,
    not_a_forecast: true,
    activation_status: "disabled",
    publish_status: "disabled",
    external_campaign_id: null,
    analytical_evidence: _buildAnalyticalEvidence(o),
    growth_opportunity: _buildGrowthOpportunity(o),
    marketing_recommendation: _buildMarketingRecommendation(r),
    campaign_assumptions: _buildCampaignAssumptions(c),
    simulated_outcomes: _buildSimulatedOutcomes(x),
    transparency: {
      historical_data: "synthetic",
      campaign: "fictional",
      outcomes: "simulated",
      outcomes_observed: false,
      outcomes_predictive: false,
    },
    meta: {
      is_demo: true,
      provenance: "synthetic_demo",
      provenance_label: "DONNÉES DE DÉMONSTRATION",
      affiliation: "none",
      simulation: true,
      simulation_label: "SIMULATION",
    },
    disclaimers: {
      opportunity: o.disclaimer,
      recommendation: r.disclaimer,
      campaign: c.disclaimer,
      outcomes: x.disclaimer,
      summary: PILOT_SUMMARY_DISCLAIMER,
    },
  };
}

export const _summaryInternals = {
  _isValidPeriod,
  _demoMetaConform,
  _simMetaConform,
  _opportunityStructureValid,
  _recommendationStructureValid,
  _campaignStructureValid,
  _outcomesStructureValid,
};
